import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatJakartaEndOfDay, formatJakartaStartOfDay } from "../../utils/date";
import { forceLogoutToLogin, isUnauthorizedApiResponse } from "../../utils/unauthorized";
import {
  selectEventStatusCounts,
  selectEventsFilters,
  selectEventsPagination,
  selectEventsSort,
  setDateRangeFilter,
  setSearchFilter,
  setStatusFilter,
} from "../../store/slices/eventsSlice";
import { selectAccessToken } from "../../store/slices/authSlice";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const statusChips = [
  { label: "All", value: "ALL" },
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const EventFilters = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectEventsFilters);
  const pagination = useSelector(selectEventsPagination);
  const sort = useSelector(selectEventsSort);
  const counts = useSelector(selectEventStatusCounts);
  const accessToken = useSelector(selectAccessToken);
  const [searchValue, setSearchValue] = useState(filters.search);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = Boolean(menuAnchor);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.append("page", pagination.page + 1);
    params.append("pageSize", pagination.pageSize);
    params.append("sortBy", sort.field);
    params.append("sortDirection", sort.direction);
    if (filters.search.trim()) {
      params.append("search", filters.search.trim());
    }
    if (filters.status !== "ALL") {
      params.append("status", filters.status);
    }
    if (filters.startDate) {
      params.append("startDate", formatJakartaStartOfDay(filters.startDate));
    }
    if (filters.endDate) {
      params.append("endDate", formatJakartaEndOfDay(filters.endDate));
    }
    return params.toString();
  }, [filters, pagination, sort]);

  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setSearchFilter(searchValue.trim()));
    }, 350);
    return () => clearTimeout(timer);
  }, [dispatch, searchValue]);

  const handleStatusChip = (value) => {
    if (filters.status === value) return;
    dispatch(setStatusFilter(value));
  };

  const handleDateChange = (key) => (event) => {
    const value = event.target.value || null;
    dispatch(
      setDateRangeFilter({
        startDate: key === "startDate" ? value : filters.startDate,
        endDate: key === "endDate" ? value : filters.endDate,
      })
    );
  };

  const handleOpenDatePicker = (ref) => () => {
    const input = ref.current;
    if (!input) return;
    const wasReadOnly = input.readOnly;
    input.readOnly = false;
    if (input.showPicker) {
      try {
        input.showPicker();
      } catch (error) {
        input.focus();
      }
    } else {
      input.focus();
    }
    input.readOnly = wasReadOnly;
  };

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/events/export?${queryString}`,
        {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );

      if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      }

      if (!response.ok) {
        let message = "Failed to download events.";
        try {
          const payload = await response.json();
          if (isUnauthorizedApiResponse(response, payload)) {
            forceLogoutToLogin();
            return;
          }
          if (payload?.resultDesc) message = payload.resultDesc;
        } catch (_) {
          // ignore json parse error
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `events-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error.message || "Failed to download events.");
    } finally {
      setExporting(false);
      setMenuAnchor(null);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          overflowX: "auto",
          py: 0.5,
          px: 0.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {statusChips.map((chip) => {
          const isActive = filters.status === chip.value;
          return (
            <Chip
              key={chip.value}
              label={
                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography component="span" fontWeight={600}>
                    {chip.label}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 24,
                      height: 20,
                      borderRadius: 999,
                      backgroundColor: isActive
                        ? "rgba(255,255,255,0.24)"
                        : "rgba(15, 23, 42, 0.06)",
                      color: isActive ? "#ffffff" : "text.secondary",
                      px: 1,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {counts[chip.value] ?? 0}
                  </Box>
                </Box>
              }
              color={isActive ? "primary" : "default"}
              variant={isActive ? "filled" : "outlined"}
              onClick={() => handleStatusChip(chip.value)}
              sx={{
                borderRadius: 10,
                height: 38,
                fontWeight: 600,
                color: isActive ? "primary.contrastText" : "text.secondary",
              }}
            />
          );
        })}
      </Stack>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ lg: "center" }}
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: { xs: "100%", lg: "auto" }, flexGrow: 1 }}
        >
          <TextField
            fullWidth
            type="date"
            label="Start Date"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate || ""}
            onChange={handleDateChange("startDate")}
            inputRef={startInputRef}
            onClick={handleOpenDatePicker(startInputRef)}
            InputProps={{
              readOnly: true,              
            }}
            inputProps={{ style: { cursor: "pointer" } }}
          />
          <TextField
            fullWidth
            type="date"
            label="End Date"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate || ""}
            onChange={handleDateChange("endDate")}
            inputRef={endInputRef}
            onClick={handleOpenDatePicker(endInputRef)}
            InputProps={{
              readOnly: true,              
            }}
            inputProps={{ style: { cursor: "pointer" } }}
          />
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
            width: { xs: "100%", lg: "auto" },
            flexGrow: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search by ID or name"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              width: 48,
              height: 48,
              flexShrink: 0,
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={openMenu}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              elevation: 6,
              sx: {
                boxShadow: "0px 22px 45px rgba(15, 23, 42, 0.18)",
                border: "1px solid rgba(148, 163, 184, 0.16)",
                background:
                  "radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff",
                borderRadius: 3,
                mt: 1,
              },
            }}
          >
            <MenuItem onClick={handleExport} disabled={exporting}>
              <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
              {exporting ? "Preparing..." : "Download Excel"}
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
      {exportError && (
        <Typography variant="body2" color="error" sx={{ mt: -1 }}>
          {exportError}
        </Typography>
      )}
    </Stack>
  );
};

export default EventFilters;
